import React, { useEffect, useState } from "react";
import { loadProjectTasks, isTasksUnavailableError } from "../../helpers/tasks/projectTasks";
import { formatDuration, formatWorkedMinutes, normalizeProgress, progressValue } from "../../helpers/tasks/taskDuration";
import { PROGRESS_FAILED_MESSAGE, PROGRESS_OFFLINE_MESSAGE } from "../../helpers/tasks/taskProgress";
import { TASK_STATUS } from "../../helpers/tasks/taskModel";
import { ProgressBar } from "./TaskParts";
import { useWorkSchedule } from "../../hooks/useWorkSchedule";
import "./tasks.css";

/**
 * Stop Session → optional task progress for the session's project. Lists ALL
 * active tasks of the project (no assignee filter). Shows planned duration,
 * previous progress, this session's time (the session is not split per task)
 * and an optional new progress value. Nothing is inferred from time worked.
 */
export function SessionTaskProgressPanel({ actor, projectId, sessionMinutes, offline, drafts, onDraftsChange, onTasksLoaded }) {
  const [state, setState] = useState({ loading: true, tasks: [], fromDevice: false, error: "" });
  const { minutesPerDay } = useWorkSchedule();

  useEffect(() => {
    let cancelled = false;
    if (!projectId) {
      setState({ loading: false, tasks: [], fromDevice: false, error: "" });
      return undefined;
    }
    setState((s) => ({ ...s, loading: true }));
    loadProjectTasks(actor, projectId)
      .then((result) => {
        if (cancelled) return;
        setState({ loading: false, tasks: result.tasks || [], fromDevice: Boolean(result.fromDevice), error: "" });
        onTasksLoaded?.(result.tasks || []);
      })
      .catch((error) => {
        if (cancelled) return;
        setState({
          loading: false,
          tasks: [],
          fromDevice: false,
          error: isTasksUnavailableError(error) ? "" : "Tasks couldn't load. You can still submit your time.",
        });
        onTasksLoaded?.([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (!projectId) return null;
  if (!state.loading && !state.tasks.length && !state.error) return null;
  const disabled = Boolean(offline);

  return (
    <>
      <div className="kpi-section-label">Task Progress</div>
      <div className="kpi-card sw-section tk-sessprog" data-testid="session-task-progress">
        <p className="tk-sessprog__intro">
          Optional. Report how far each task is. Your time is saved either way.
          <span className="tk-sessprog__time" data-testid="session-task-progress-time">
            This session: {formatWorkedMinutes(sessionMinutes)}
          </span>
        </p>
        {disabled ? (
          <div className="tk-notice" role="status" data-testid="session-task-progress-offline">
            {PROGRESS_OFFLINE_MESSAGE}
          </div>
        ) : null}
        {state.fromDevice && !disabled ? (
          <div className="tk-hint">Showing the copy saved on this device.</div>
        ) : null}
        {state.error ? <div className="tk-hint" role="status">{state.error}</div> : null}
        {state.loading ? <div className="tk-hint">Loading tasks…</div> : null}
        <ul className="tk-sessprog__list">
          {state.tasks.map((task) => {
            const previous = progressValue(task);
            const raw = drafts[task.project_task_id] ?? "";
            const value = normalizeProgress(raw);
            const invalid = raw !== "" && (value == null || Number.isNaN(value));
            const duration = formatDuration(task.planned_duration_minutes, minutesPerDay);
            const inputId = `tk-sessprog-${task.project_task_id}`;
            return (
              <li key={task.project_task_id} className="tk-sessprog__item" data-testid="session-task-progress-row">
                <div className="tk-sessprog__head">
                  <label htmlFor={inputId} className={`tk-sessprog__title${task.status === TASK_STATUS.COMPLETED ? " is-done" : ""}`}>
                    {task.title}
                  </label>
                  <span className="tk-sessprog__meta">
                    {duration ? `Planned ${duration}` : "No planned duration"} · Previous{" "}
                    {previous == null ? "not reported" : `${previous}%`}
                  </span>
                  {previous != null ? <ProgressBar value={previous} /> : null}
                </div>
                <div className="tk-sessprog__input">
                  <input
                    id={inputId}
                    className="rec-input"
                    inputMode="numeric"
                    placeholder={previous == null ? "—" : String(previous)}
                    value={raw}
                    disabled={disabled}
                    aria-invalid={invalid || undefined}
                    aria-label={`New progress for ${task.title} (percent)`}
                    onChange={(e) =>
                      onDraftsChange({ ...drafts, [task.project_task_id]: e.target.value.replace(/[^\d]/g, "").slice(0, 3) })
                    }
                  />
                  <span className="tk-sessprog__pct">%</span>
                  <button
                    type="button"
                    className="tk-sessprog__done"
                    disabled={disabled}
                    onClick={() => onDraftsChange({ ...drafts, [task.project_task_id]: "100" })}
                  >
                    Done
                  </button>
                </div>
                {invalid ? <div className="tk-sessprog__err">Enter 0 to 100.</div> : null}
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
}

/** Non-blocking result after the session is saved (never blocks Mission Complete). */
export function SessionTaskProgressNotice({ outcome, onRetry }) {
  if (!outcome || outcome.status === "idle") return null;
  if (outcome.status === "saving") {
    return <p className="tk-sessprog__note" role="status">Saving task progress…</p>;
  }
  if (outcome.status === "saved") {
    return (
      <p className="tk-sessprog__note is-ok" role="status" data-testid="session-task-progress-saved">
        Task progress saved ({outcome.saved.length}).
      </p>
    );
  }
  const message = outcome.status === "offline" ? "Task progress was not saved (offline). Your time was saved." : PROGRESS_FAILED_MESSAGE;
  return (
    <div className="tk-sessprog__note is-warn" role="alert" data-testid="session-task-progress-failed">
      <span>{message}</span>
      {outcome.failed?.length ? (
        <ul>
          {outcome.failed.map((f) => (
            <li key={f.taskId}>
              {f.title || "Task"}: {f.message}
            </li>
          ))}
        </ul>
      ) : null}
      {onRetry ? (
        <button type="button" className="nx-btn nx-btn-outline" onClick={onRetry} data-testid="session-task-progress-retry">
          Retry task progress
        </button>
      ) : null}
    </div>
  );
}
